import React, { useCallback, useEffect } from 'react';
import styles from './CampoSeletor.module.css';
import Icon from '@mdi/react';
import {
  mdiChevronDown,
  mdiChevronUp,
  mdiClose,
  mdiCross,
  mdiDelete,
  mdiInformationSlabCircleOutline,
  mdiMagnify,
} from '@mdi/js';
import ReactDOM from 'react-dom';
import { throttle } from '../../lib/util';
import CampoTexto from './CampoTexto';
import ReactDOMServer from 'react-dom/server';
import HBox from '../layout/HBox';
import { Tooltip } from '../layout/Tooltip';
import { Select } from '@base-ui/react/select';

export type CampoSeletorOption = {
  id: string;
  label: React.ReactNode;
  group?: string;
  error?: boolean;
};

type CampoSeletorProps = {
  value?: string | null;
  onChange?: (value: string | null) => void;
  label?: string;
  placeholder?: string;
  type?: string;
  required?: boolean;
  options: CampoSeletorOption[];
  disabled?: boolean;
  error?: string;
  hint?: string;
  noSearch?: boolean;
  noBorder?: boolean;
  tooltipText?: React.ReactNode;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  instanceId?: string;
  nullable?: boolean;
  [key: string]: any;
};

export default function CampoSeletor({
  value,
  onChange,
  label,
  placeholder,
  type = 'text',
  required,
  options,
  disabled,
  error,
  hint,
  noSearch,
  tooltipText,
  tooltipPosition,
  noBorder,
  instanceId,
  nullable,
  ...props
}: Readonly<CampoSeletorProps>) {
  const [search, setSearch] = React.useState<string>('');

  const filteredOptions =
    search.length > 0
      ? options?.filter(
        it =>
          !it.label ||
          ReactDOMServer.renderToString(it.label)
            .toLowerCase()
            .includes(search.toLowerCase()),
      )
      : options;

  const selected = options?.find(it => it.id === value);

  const groups = filteredOptions?.reduce<{
    [key: string]: CampoSeletorOption[];
  }>((acc, it) => {
    if (it.group) {
      acc[it.group] = acc[it.group] ?? [];
      acc[it.group].push(it);
    } else {
      acc[''] = acc[''] ?? [];
      acc[''].push(it);
    }
    return acc;
  }, {});

  return (
    <div style={{ width: `100%`, position: 'relative', overflow: 'visible' }}>
      {label && (
        <label className={styles.label}>
          <HBox stretch>
            <span>{label}</span>
            {tooltipText && (
              <Tooltip text={tooltipText} position={tooltipPosition}>
                <Icon
                  path={mdiInformationSlabCircleOutline}
                  size="14px"
                  color="var(--tc-color-info)"
                />
              </Tooltip>
            )}
          </HBox>
        </label>
      )}
      <Select.Root
        value={value ?? undefined}
        onValueChange={(newValue) => onChange?.(newValue ?? null)}
        disabled={disabled}
        modal={false}
      >
        <Select.Trigger
          id={instanceId}
          className={[
            styles.container,
            error ? styles.error : null,
            disabled ? styles.disabled : null,
            props.className ?? null,
          ].join(' ')}
          style={{ border: noBorder ? 'none' : undefined }}
        >
          <div className={styles.selectedContainer}>
            <Select.Value placeholder={placeholder}>
              {(val) => options?.find(opt => opt.id === val)?.label}
            </Select.Value>
          </div>
          {nullable && value != null && (
            <span
              onClick={(e: any) => {
                e.stopPropagation();
                onChange?.(null);
              }}
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                padding: '4px',
              }}
            >
              <Icon path={mdiClose} size="20px" />
            </span>
          )}
          <Select.Icon className={styles.arrow}>
            <Icon path={mdiChevronDown} size={1} />
          </Select.Icon>
        </Select.Trigger>
        <Select.Portal>
          <Select.Positioner
            className={styles.positioner}
            alignItemWithTrigger={false}
            side="bottom"
            align="start"
            sideOffset={4}
          >
            <Select.Popup className={styles.options}>
              <Select.List>
                {groups && Object.entries(groups).map(([group, gOptions], gidx) => (
                  <Select.Group key={gidx}>
                    {group && <div className={styles.group}>{group}</div>}
                    {gOptions.map((option) => (
                      <Select.Item
                        key={option.id}
                        value={option.id}
                        className={[
                          styles.option,
                          group.length > 0 ? styles.grouped : null,
                          option.error ? styles.error : null,
                        ].join(' ')}
                      >
                        <Select.ItemText>{option.label}</Select.ItemText>
                      </Select.Item>
                    ))}
                  </Select.Group>
                ))}
              </Select.List>
            </Select.Popup>
          </Select.Positioner>
        </Select.Portal>
      </Select.Root>
      {hint && !error && <div className={styles.hint}>{hint}</div>}
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
}
